import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  useGetLocationGroup,
  useGetPaginatedLocationsForLocationGroup,
  useParamQuery,
} from 'src/hooks';
import { formatQuery, lang } from 'src/utils';

export const useLocationGroupEditPage = () => {
  const [initialLocations, setInitialLocations] = useState<object[]>([]);
  const { locationGroupId = '' } = useParams();
  const location = useLocation();
  const filtersFromQuery = formatQuery(location.search);
  const { onChangeParams, getParamsValue } = useParamQuery({
    page: ['1'],
  });
  const pageParam = Number(getParamsValue('page')?.[0] || 1);
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const nonPageFilters = filtersFromQuery.filter(
    (filter) => !filter.startsWith('page='),
  );
  const filters = [
    `location_group_id=${locationGroupId}`,
    `page=${page}`,
    ...nonPageFilters,
  ];

  const columns = useMemo(
    () => [
      { id: 'name', label: lang.locationsOfGroupText },
      { id: 'store_number', label: lang.storeNumber },
      { id: 'actions', label: '' },
    ],
    [],
  );

  const {
    data: locationGroupData,
    isLoading: isLoadingLocationGroup,
    error,
  } = useGetLocationGroup(locationGroupId);

  const [locationGroupName, setLocationGroupName] = React.useState<string>('');
  useEffect(() => {
    setLocationGroupName(locationGroupData?.name.trim() as string);
  }, [locationGroupData]);

  const { data, isLoading: isLoadingLocations } =
    useGetPaginatedLocationsForLocationGroup(filters);

  useEffect(() => {
    if (data?.locations && initialLocations.length === 0 && page === 1) {
      setInitialLocations(data?.locations);
    }
  }, [data, initialLocations, page]);

  const locations = data?.locations ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalCount = data?.totalCount ?? 0;

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    onChangeParams('page', [`${value}`]);
  };

  const deleteGroupTooltip = isEmpty(initialLocations)
    ? ''
    : `${lang.deleteGroupTooltip}`;

  return {
    locationGroupId,
    locations,
    isLoadingLocations,
    locationGroupName,
    isLoadingLocationGroup,
    locationGroupData,
    error,
    columns,
    setLocationGroupName,
    deleteGroupTooltip,
    totalPages,
    totalCount,
    page,
    handlePageChange,
    initialLocations,
  };
};
